/**
 * Detail Book / workstation — Lab `workstation.js` API set via CM-API.
 *
 * - `getTotalBooks1` — particular books (runners + root NE)
 * - `getBooksForBackend` — market-scoped event group (metadata / fallback)
 * - `getMemberNetExposure` — headline NE for the market
 * - `getBetDetailsForWorkstation` — bet ticker
 * - `getBetfairBook` / `getBetfairBookLineSession` — Betfair vs local book
 * - `get|setNetExpoPageCommissionRate` — Total(X) multiplier (Lab CONFIG-V2)
 * - Live odds: catalog `liveRates` seed + Socket-API SSE (Lab rateSocket)
 *
 * While hierarchy self is loading / unavailable → fixture book (same as demos).
 */

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cmApi, type BookEventGroupBackend } from '@/api/cmApi'
import {
  assembleWorkstationBook,
  betfairCompareFromFancy,
  betfairCompareFromMatch,
  emptyBetfairCompareMatch,
  hasDownlineBookPosition,
  isLineSessionMarket,
  marketFromBooksGroups,
  treeNodeFromDownlineBook,
  type BetDetailsForWorkstationRequest,
  type BetDetailsForWorkstationResponse,
  type BetfairBookLineSessionResponse,
  type BetfairBookResponse,
  type BetfairCompareView,
  type InvalidRealTimeBetRequest,
  type MemberNetExposureResponse,
  type RevalidRealTimeBetRequest,
  type TotalBooks1Request,
  type TotalBooks1Response,
} from '@/api/map/workstation'
import { applyLiveRatesToBook, fancyRatesFromBets, mergeLiveRatePayload } from '@/api/map/rates'
import { useBookmakerSessionFlag } from '@/api/hooks/useCmMe'
import { authHeaders } from '@/auth/session'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import {
  isDirectChildOf,
  levelToRole,
  ROLE_LABEL,
} from '@/features/hierarchy/hierarchy'
import type { BookType } from '@/features/net-exposure/fixture'
import {
  defaultBetDateRange,
  getWorkstationBook,
  type WorkstationBook,
  type WorkstationMarketType,
  type WorkstationRunner,
  type WorkstationTreeNode,
} from '@/features/net-exposure/workstation-fixture'
import { BOOKS_POLL_MS } from './useBooks'
import { useMarketRatesStream } from './useMarketRatesStream'
import {
  buildFancyPtLadderBody,
  fancyPtLadderRoute,
  fancyPtLadderUsesTotal,
} from './fancyPtLadder'

/** Lab line-session BF book polls every 5s. */
export const BETFAIR_BOOK_POLL_MS = 5_000

/** Lab bet ticker polls every 3s (workstation.js ~2–5s). */
export const BET_TICKER_POLL_MS = 3_000

const CM_LEVEL = ROLE_LABEL['company-master']

export type WorkstationQuery = {
  marketId: string
  selectedType: BookType
  /** Search-param fallbacks. */
  eventName?: string
  marketName?: string
  type?: WorkstationMarketType
  /** Net Exposure sport (Horse Racing / Greyhound Racing). */
  eventType?: string
  /** Bet ticker filters (Lab getBetList). */
  page?: number
  maxRow?: number
  fromDate?: string
  toDate?: string
  userName?: string | string[]
  currencyType?: string
}

export type WorkstationBetFilters = Pick<
  WorkstationQuery,
  'page' | 'maxRow' | 'fromDate' | 'toDate' | 'userName' | 'currencyType'
>

function useLiveIdentity() {
  const userQ = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
    staleTime: 60_000,
  })
  const user = userQ.data?.node.id
  const username = userQ.data?.node.username
  const live = Boolean(user)
  return { userQ, user, username, live }
}

async function loadTotalBooks1(
  query: WorkstationQuery,
  user: string,
  username: string | undefined,
): Promise<TotalBooks1Response> {
  const body: TotalBooks1Request = {
    user,
    marketId: query.marketId,
    loggedInMember: user,
    username: username || undefined,
    accessLevel: CM_LEVEL,
    selectedType: query.selectedType,
    page: 1,
  }
  return cmApi.getTotalBooks1(authHeaders(), body)
}

/** Body for Detail Book → `getBooksForBackend` (staff Market access on filter). */
export function workstationBooksRequest(
  query: Pick<WorkstationQuery, 'marketId' | 'selectedType'>,
  user: string,
  bookmakerSessionFlag: BookmakerSessionFlag,
) {
  return {
    selectedType: query.selectedType,
    filter: {
      user,
      level: CM_LEVEL,
      marketId: query.marketId,
      eventname: 'All',
      bookmakerSessionFlag,
    },
  }
}

async function loadBooksForMarket(
  query: WorkstationQuery,
  user: string,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<BookEventGroupBackend[]> {
  return cmApi.getBooksForBackend(
    authHeaders(),
    workstationBooksRequest(query, user, bookmakerSessionFlag),
  )
}

async function loadMemberNetExposure(
  marketId: string,
  user: string,
): Promise<MemberNetExposureResponse> {
  return cmApi.getMemberNetExposure(authHeaders(), { _id: user, marketId })
}

async function loadBetDetails(
  query: WorkstationQuery,
  user: string,
): Promise<BetDetailsForWorkstationResponse> {
  const defaults = defaultBetDateRange()
  const body: BetDetailsForWorkstationRequest = {
    user,
    marketId: query.marketId,
    accessLevel: CM_LEVEL,
    page: query.page ?? 1,
    maxRow: query.maxRow ?? 100,
    fromDate: query.fromDate || defaults.fromDate,
    toDate: query.toDate || defaults.toDate,
    currencyType: query.currencyType || 'All',
  }
  if (query.userName != null && query.userName !== '') body.userName = query.userName
  return cmApi.getBetDetailsForWorkstation(authHeaders(), body)
}

async function loadBetfairBook(
  marketId: string,
  lineSession: boolean,
): Promise<BetfairBookResponse | BetfairBookLineSessionResponse> {
  const headers = authHeaders()
  return lineSession
    ? cmApi.getBetfairBookLineSession(headers, { marketId })
    : cmApi.getBetfairBook(headers, { marketId })
}

type DownlineBookInput = {
  parentUserId: string
  marketId: string
  selectedType: BookType
  runners: WorkstationRunner[]
  marketType: WorkstationMarketType
}

/** One direct child → getTotalBooks1 (Lab getBookForworkStation per kid). */
async function loadOneDirectChildBook(
  child: { id: string; level: number; username?: string; name: string },
  input: DownlineBookInput,
): Promise<WorkstationTreeNode | null> {
  const role = levelToRole(child.level)
  const res = await cmApi.getTotalBooks1(authHeaders(), {
    user: child.id,
    marketId: input.marketId,
    loggedInMember: child.id,
    username: child.username || child.name || undefined,
    accessLevel: ROLE_LABEL[role],
    selectedType: input.selectedType,
    page: 1,
  })
  const row = res.book?.[0]
  if (!hasDownlineBookPosition(row)) return null
  return treeNodeFromDownlineBook(row!, input.runners, input.marketType, {
    username: child.username?.trim() || child.name || row!.username,
    level: role === 'player' ? 'player' : 'agent',
    accessLevel: ROLE_LABEL[role],
  })
}

/**
 * Lab `showDownLine` → `getParticularBooks` without accessLevel
 * (`getImmediateChildren` + `getBookForDownline`).
 *
 * CM-API has no downline batch yet — fan-out getTotalBooks1 across
 * **direct children only** (not full subtree).
 */
export async function loadDownlineParticularBooks(
  input: DownlineBookInput,
): Promise<WorkstationTreeNode[]> {
  const tree = await cmApi.hierarchyTree(authHeaders(), input.parentUserId)
  const kids = tree.nodes.filter((n) =>
    isDirectChildOf(n, input.parentUserId, n.parentId == null ? 0 : 1),
  )
  // ponytail: ceiling = one request per direct child until CM-API ships getBookForDownline batch.
  const pending: Promise<WorkstationTreeNode | null>[] = []
  for (const child of kids) {
    pending.push(loadOneDirectChildBook(child, input))
  }
  const nodes = await Promise.all(pending)
  return nodes
    .filter((n): n is WorkstationTreeNode => Boolean(n))
    .sort((a, b) => a.username.localeCompare(b.username))
}

async function invalidWorkstationBets(
  body: InvalidRealTimeBetRequest,
): Promise<Awaited<ReturnType<typeof cmApi.invalidRealTimeBet>>> {
  return cmApi.invalidRealTimeBet(authHeaders(), body)
}

async function revalidateWorkstationBet(
  body: RevalidRealTimeBetRequest,
): Promise<Awaited<ReturnType<typeof cmApi.revalidRealTimeBet>>> {
  return cmApi.revalidRealTimeBet(authHeaders(), body)
}

/** Lab `getParticularBooks` → `POST /v1/book/getTotalBooks1`. */
export function useTotalBooks1(query: WorkstationQuery) {
  const { user, username, live } = useLiveIdentity()
  return useQuery({
    queryKey: [
      'cm',
      'workstation-total-books',
      {
        marketId: query.marketId,
        selectedType: query.selectedType,
        user,
        level: CM_LEVEL,
      },
    ],
    queryFn: () => loadTotalBooks1(query, user!, username),
    enabled: Boolean(live && query.marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab family-icon expand — downline books for one parent on this market.
 * Polls while open (Lab `timeoutChildren` refresh on book change).
 */
export function useDownlineParticularBooks(input: {
  parentUserId: string
  marketId: string
  selectedType: BookType
  runners: WorkstationRunner[]
  marketType: WorkstationMarketType
  enabled: boolean
}) {
  return useQuery({
    queryKey: [
      'cm',
      'workstation-downline-books',
      {
        parentUserId: input.parentUserId,
        marketId: input.marketId,
        selectedType: input.selectedType,
      },
    ],
    queryFn: () =>
      loadDownlineParticularBooks({
        parentUserId: input.parentUserId,
        marketId: input.marketId,
        selectedType: input.selectedType,
        runners: input.runners,
        marketType: input.marketType,
      }),
    enabled: Boolean(input.enabled && input.parentUserId && input.marketId),
    refetchInterval: input.enabled ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab workstation `getBook` with marketId → `POST /v1/book/getBooksForBackend`. */
export function useWorkstationBooks(query: WorkstationQuery) {
  const { user, live } = useLiveIdentity()
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  return useQuery({
    queryKey: [
      'cm',
      'workstation-books',
      {
        marketId: query.marketId,
        selectedType: query.selectedType,
        user,
        level: CM_LEVEL,
        bookmakerSessionFlag,
      },
    ],
    queryFn: () => loadBooksForMarket(query, user!, bookmakerSessionFlag),
    enabled: Boolean(live && query.marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getMarketNetExposure` → `POST /v1/book/getMemberNetExposure`. */
export function useMemberNetExposure(marketId: string) {
  const { user, live } = useLiveIdentity()
  return useQuery({
    queryKey: ['cm', 'workstation-member-ne', { marketId, user }],
    queryFn: () => loadMemberNetExposure(marketId, user!),
    enabled: Boolean(live && marketId),
    refetchInterval: live ? BOOKS_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/** Lab `getBetList` → `POST /v1/bets/getBetDetailsForWorkstation`. */
export function useBetDetailsForWorkstation(query: WorkstationQuery) {
  const { user, live } = useLiveIdentity()
  return useQuery({
    queryKey: [
      'cm',
      'workstation-bets',
      {
        marketId: query.marketId,
        user,
        level: CM_LEVEL,
        page: query.page ?? 1,
        maxRow: query.maxRow ?? 100,
        fromDate: query.fromDate,
        toDate: query.toDate,
        userName: query.userName,
        currencyType: query.currencyType,
      },
    ],
    queryFn: () => loadBetDetails(query, user!),
    enabled: Boolean(live && query.marketId),
    refetchInterval: live ? BET_TICKER_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Lab `getBetfairBook` / `getBetfairBookLineSession`.
 * Soft-fail (retry: false) — Betfair may 503 when BETFAIR_API_URL is unset.
 */
export function useBetfairBook(input: {
  marketId: string
  /** Prefer Lab raw type (`lineLadder`); UI maps line → fancy layout. */
  marketType?: WorkstationMarketType | string
  marketName?: string
  lineLadder?: boolean
}) {
  const { live } = useLiveIdentity()
  const lineSession = isLineSessionMarket({
    marketType: input.marketType,
    marketId: input.marketId,
    marketName: input.marketName,
    lineLadder: input.lineLadder,
  })
  return useQuery({
    queryKey: ['cm', 'workstation-betfair-book', { marketId: input.marketId, lineSession }],
    queryFn: () => loadBetfairBook(input.marketId, lineSession),
    enabled: Boolean(live && input.marketId),
    refetchInterval: live ? (lineSession ? BETFAIR_BOOK_POLL_MS : BOOKS_POLL_MS) : false,
    retry: false,
    placeholderData: (previous) => previous,
  })
}

export function useInvalidateWorkstationBets() {
  const client = useQueryClient()
  const { username } = useLiveIdentity()

  return useMutation({
    mutationFn: (input: { betIds: string[]; reason: string }) =>
      invalidWorkstationBets({
        _id: input.betIds,
        reason: input.reason,
        invalidBy: username || undefined,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['cm', 'workstation-total-books'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-books'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-member-ne'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-bets'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-betfair-book'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-downline-books'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-pt-ladder'] })
    },
  })
}

/**
 * Lab `getRatesOfTotalMulti` → CONFIG-V2 `getNetExpoPageCommissionRate`.
 * Offline / no CM session → disabled (UI keeps default 12).
 */
export function useNetExpoPageCommissionRate() {
  const { live } = useLiveIdentity()
  const query = useQuery({
    queryKey: ['cm', 'net-expo-page-commission-rate'],
    queryFn: () => cmApi.getNetExpoPageCommissionRate(authHeaders()),
    enabled: live,
    staleTime: 60_000,
  })
  return { ...query, live }
}

/**
 * Lab `setRatesOfTotalMulti` → CONFIG-V2 `setNetExpoPageCommissionRate`.
 */
export function useSetNetExpoPageCommissionRate() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (commissionRate: number) =>
      cmApi.setNetExpoPageCommissionRate(authHeaders(), { commissionRate }),
    onSuccess: (res) => {
      if (Number.isFinite(res.value) && res.value > 0) {
        client.setQueryData(['cm', 'net-expo-page-commission-rate'], res)
      }
      client.invalidateQueries({ queryKey: ['cm', 'net-expo-page-commission-rate'] })
    },
  })
}

export function useRevalidateWorkstationBet() {
  const client = useQueryClient()
  const { username } = useLiveIdentity()

  return useMutation({
    mutationFn: (input: { betId: string }) =>
      revalidateWorkstationBet({
        _id: input.betId,
        revalidBy: username || undefined,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['cm', 'workstation-total-books'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-books'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-member-ne'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-bets'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-betfair-book'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-downline-books'] })
      client.invalidateQueries({ queryKey: ['cm', 'workstation-pt-ladder'] })
    },
  })
}

/** Lab PT ladder poll while open (~1.5s). */
export const PT_LADDER_POLL_MS = 1_500

/**
 * Lab `showRunners` → `Book/getLadder` or `getLadderTotal`.
 * Total Book + non-player uses getLadderTotal (player lt/gt flipped).
 */
export function useFancyPtLadder(input: {
  marketId: string
  userId: string
  accessLevel?: string
  selectedType: BookType
  nodeLevel: 'agent' | 'player'
  enabled: boolean
  /** Betfair line session → ladder body type betfair-line-sessions. */
  lineSession?: boolean
}) {
  const totalBook = fancyPtLadderUsesTotal(input.selectedType, input.nodeLevel)
  const lineSession = Boolean(input.lineSession)
  return useQuery({
    queryKey: [
      'cm',
      'workstation-pt-ladder',
      {
        marketId: input.marketId,
        userId: input.userId,
        accessLevel: input.accessLevel,
        selectedType: input.selectedType,
        totalBook,
        lineSession,
      },
    ],
    queryFn: async () => {
      const body = buildFancyPtLadderBody({
        marketId: input.marketId,
        userId: input.userId,
        accessLevel: input.accessLevel,
        selectedType: input.selectedType,
        nodeLevel: input.nodeLevel,
        lineSession,
      })
      return fancyPtLadderRoute(input.selectedType, input.nodeLevel) === 'getLadderTotal'
        ? cmApi.getLadderTotal(authHeaders(), body)
        : cmApi.getLadder(authHeaders(), body)
    },
    enabled: Boolean(input.enabled && input.marketId && input.userId),
    refetchInterval: input.enabled ? PT_LADDER_POLL_MS : false,
    placeholderData: (previous) => previous,
  })
}

/**
 * Compose Lab workstation payloads into one `WorkstationBook`.
 * Offline / no CM user → fixture. Live with empty APIs → null book (empty UI).
 */
export function useWorkstationBook(query: WorkstationQuery) {
  const { live, username } = useLiveIdentity()
  const totalQ = useTotalBooks1(query)
  const booksQ = useWorkstationBooks(query)
  const neQ = useMemberNetExposure(query.marketId)
  const betsQ = useBetDetailsForWorkstation(query)
  const ratesQ = useMarketRatesStream(live ? query.marketId : undefined)

  const fixture = useMemo(
    () => (query.marketId ? getWorkstationBook(query.marketId) : undefined),
    [query.marketId],
  )

  const book = useMemo((): WorkstationBook | null | undefined => {
    if (!live) return fixture ?? null
    const booksMarket = marketFromBooksGroups(booksQ.data, query.marketId)
    const assembled = assembleWorkstationBook({
      marketId: query.marketId,
      bookType: query.selectedType,
      total: totalQ.data,
      booksMarket,
      bets: betsQ.data,
      memberNe: neQ.data?.netExposure,
      fallback: {
        eventName: query.eventName || fixture?.eventName,
        marketName: query.marketName || fixture?.marketName,
        type: query.type || fixture?.type,
        eventType: query.eventType || fixture?.eventType,
        username,
        accessLevel: CM_LEVEL,
      },
    })
    if (!assembled) return assembled
    // Dev/demo: when live bet ticker is empty but we have fixture rows (incl. RT_INVALID_BET demo),
    // keep Backend-UI parity so invalid/revalidate controls are visible locally.
    const withBets =
      assembled.bets.length === 0 && fixture?.bets.length && import.meta.env.DEV
        ? { ...assembled, bets: fixture.bets }
        : assembled
    // Catalog HTTP seed (CM getTotalBooks1.liveRates) = Frontend Player-Markets
    // first paint. SSE overlays on top; fancy can still seed from open bets.
    let ratesPayload = ratesQ.payload
    const catalogRates = totalQ.data?.liveRates ?? null
    if (catalogRates) {
      ratesPayload = ratesPayload
        ? mergeLiveRatePayload(catalogRates, ratesPayload)
        : catalogRates
    }
    // Custom fancy only — BF Line Session uses live EX ladder (not bet Yes/No seed).
    if (withBets.type === 'fancy' && !withBets.lineLadder) {
      const fromBets = fancyRatesFromBets(withBets.bets)
      if (fromBets) {
        ratesPayload = ratesPayload
          ? mergeLiveRatePayload(fromBets, ratesPayload)
          : fromBets
      }
    }
    if (!ratesPayload) return withBets
    return applyLiveRatesToBook(withBets, ratesPayload)
  }, [
    live,
    fixture,
    query.marketId,
    query.selectedType,
    query.eventName,
    query.marketName,
    query.type,
    query.eventType,
    totalQ.data,
    booksQ.data,
    betsQ.data,
    neQ.data?.netExposure,
    username,
    ratesQ.payload,
  ])

  const marketType = book?.type ?? query.type
  const marketName = book?.marketName || query.marketName
  const booksMarket = marketFromBooksGroups(booksQ.data, query.marketId)
  const labBookType = totalQ.data?.type
  const lineLadderFlag = Boolean(
    book?.lineLadder ||
      totalQ.data?.book?.[0]?.lineLadder ||
      booksMarket?.lineLadder ||
      labBookType === 'lineLadder',
  )
  const lineSession = isLineSessionMarket({
    marketType: labBookType ?? marketType,
    marketId: query.marketId,
    marketName,
    lineLadder: lineLadderFlag,
  })
  const betfairQ = useBetfairBook({
    marketId: query.marketId,
    marketType: labBookType ?? marketType,
    marketName,
    lineLadder: lineLadderFlag,
  })

  const betfairCompare = useMemo((): BetfairCompareView | null => {
    if (!live) return null
    const runners = book?.runners ?? []
    // Lab always paints Match Odds Betfair Book rows — 0/0 when API empty/error.
    if (!betfairQ.data) {
      return !lineSession && runners.length > 0
        ? emptyBetfairCompareMatch(runners)
        : null
    }
    const data = betfairQ.data
    if (lineSession) {
      const line = data as BetfairBookLineSessionResponse
      return betfairCompareFromFancy(
        line.bookFromLocal,
        line.bookFromBetfair,
        marketName || query.marketId,
      )
    }
    const match = data as BetfairBookResponse
    return (
      betfairCompareFromMatch(match.bookFromLocal, match.bookFromBetfair, runners) ??
      (runners.length > 0 ? emptyBetfairCompareMatch(runners) : null)
    )
  }, [
    live,
    betfairQ.data,
    lineSession,
    marketName,
    book?.runners,
    query.marketId,
  ])

  const isLoading =
    live &&
    !totalQ.data &&
    !booksQ.data &&
    (totalQ.isLoading || booksQ.isLoading)

  const isError = live && totalQ.isError && booksQ.isError
  const error = totalQ.error ?? booksQ.error ?? betsQ.error ?? neQ.error
  const isFetching =
    totalQ.isFetching ||
    booksQ.isFetching ||
    betsQ.isFetching ||
    neQ.isFetching ||
    betfairQ.isFetching

  return {
    book: book === undefined ? null : book,
    memberNetExposure: live ? (neQ.data?.netExposure ?? null) : null,
    betfairCompare,
    betCount: live ? (betsQ.data?.count ?? book?.bets.length ?? 0) : (book?.bets.length ?? 0),
    isLoading,
    isError,
    error,
    isFetching,
    live,
    refetch: () => {
      void totalQ.refetch()
      void booksQ.refetch()
      void neQ.refetch()
      void betsQ.refetch()
      void betfairQ.refetch()
    },
    ratesStatus: ratesQ.status,
  }
}
